import { gql } from "@apollo/client"

export const VISUAL_FRAGMENT = gql`
  fragment VisualFragment on VisualRefType {
    id
    url
    name
    type
    altTag
    usage
  }
`

const PRICING_FIELDS = gql`
  fragment PricingFields on ProductPricingDetailsType {
    formatted
    raw
    label
    currency
    currencySymbol
  }
`

export const PLP_PRICING_FRAGMENT = gql`
  fragment PlpPricingFragment on ProductCardPricing {
    primary {
      ...PricingFields
    }
    secondary {
      ...PricingFields
    }
  }
  ${PRICING_FIELDS}
`

export const HOW_TO_USE_SLIDES_FRAGMENT = gql`
  fragment HowToUseSlidesFragment on VariantHowToUseSlidesType {
    description
    imageOrVideo {
      ... on VisualBynderType {
        bynderId: id
        imageDescription: description
        imageName: name
        thumbnails {
          size_4_5_web
          size_16_9_web
          size_240
          size_375
          size_460
          size_OTF_BETA
          size_mini
          size_thul
          size_webimage
        }
      }
      ... on VideoAssetType {
        videoId: id
        videoType
        imageUrl
        sources {
          uri
          key
          contentType
        }
        videoName: name
        videoDescription: description
        tags
        position
      }
    }
  }
`

export const MEMBERSHIP_FRAGMENT = gql`
  fragment MembershipFragment on MembershipType {
    id
    value
    duration
    name
    description
    visual {
      ...VisualFragment
    }
    giftVisual {
      ...VisualFragment
    }
    price
    period
    frequency
    initEntitlement
    monthlyAllowance
    segment
  }
  ${VISUAL_FRAGMENT}
`

export const PRICING_FRAGMENT = gql`
  fragment PricingFragment on ProductPricingType {
    members
    typical
    guest
  }
`

export const INCLUDED_DISCOUNTS_FRAGMENT = gql`
  fragment IncludedDiscountsFragment on IncludedDiscountType {
    type
    state
    name
    value
    code
    target
    referralCode
  }
`
export const REJECTED_DISCOUNTS_FRAGMENT = gql`
  fragment RejectedDiscountFragment on RejectedDiscountType {
    code
    origin
    state
  }
`

export const BASKET_PRICING_FRAGMENT = gql`
  fragment BasketPricingFragment on BasketPricingType {
    priceTier
    totalMembershipPrice
    totalMembershipDiscountedPrice
    estimatedShipping
    shipping {
      net
      gross
      tax
    }
    totalProductPriceByTier {
      member
      typical
    }
    totalPrice {
      net
      gross
      tax
    }
    subTotal {
      net
      gross
      tax
    }
    totalDiscountsByType {
      type
      state
      name
      value
      code
      target
    }
    totalInsiderPrice {
      net
      gross
      tax
    }
    totalInsiderDiscountPrice
  }
`

export const AVAILABILITY_FRAGMENT = gql`
  fragment AvailabilityFragment on ProductAvailabilityType {
    status
    reordered
    expectedOn
    stockCount
    label
  }
`

export const TAG_FRAGMENT = gql`
  fragment TagFragment on TagType {
    id
    name
    target
  }
`

export const BYNDER_VISUAL_FRAGMENT = gql`
  fragment BynderVisualFragment on VisualBynderType {
    id
    description
    name
    thumbnails {
      size_4_5_web
      size_16_9_web
      size_240
      size_375
      size_460
      size_OTF_BETA
      size_mini
      size_thul
      size_webimage
    }
  }
`

export const BADGE_FRAGMENT = gql`
  fragment BadgeFragment on BadgeType {
    badge
    text
    fromDate
    toDate
    media {
      ...BynderVisualFragment
    }
  }
  ${BYNDER_VISUAL_FRAGMENT}
`

export const FORMULA_FRAGMENT = gql`
  fragment FormulaFragment on VariantFormulaType {
    keyDescription
    ingredients
    formulatedWith {
      label
      value
    }
    formulatedWithout {
      label
      value
    }
    keyIngredients {
      label
      value
    }
    media {
      ...BynderVisualFragment
    }
  }
  ${BYNDER_VISUAL_FRAGMENT}
`

export const HOWTO_FRAGMENT = gql`
  fragment HowToFragment on VariantHowToType {
    title
    description
    icons {
      label
      value
    }
  }
`

export const COUNTRY_FRAGMENT = gql`
  fragment CountryFragment on CountryType {
    code
    name
  }
`

export const LABEL_FRAGMENT = gql`
  fragment LabelFragment on LabelValueType {
    label
    value
  }
`

export const STANDARDS_FRAGMENT = gql`
  fragment StandardsFragment on VariantStandardsType {
    vegan
    crueltyFree
    cleanBeauty
    pcrpir
    fullyRecyclable
    partlyRecyclable
    fragranceFree
    dermatologistApproved
    clinicallyTested
    plasticNeutral
  }
`

export const REVIEW_FRAGMENT = gql`
  fragment ReviewFragment on ReviewType {
    stars
    title
    comment
    date
    checked
    authorName
  }
`

export const REVIEW_SUMMARY_FRAGMENT = gql`
  fragment ReviewSummaryFragment on ReviewSummaryType {
    count
    stars
    bestRating
    worstRating
    reviews {
      ...ReviewFragment
    }
  }
  ${REVIEW_FRAGMENT}
`

export const TRIALS_FRAGMENT = gql`
  fragment TrialsFragment on VariantTrialType {
    title
    trialResults
    sourceOfTrialResults
    results {
      label
      value
    }
    beforeImage {
      ...BynderVisualFragment
    }
    afterImage {
      ...BynderVisualFragment
    }
  }
  ${BYNDER_VISUAL_FRAGMENT}
`

export const UGC_FRAGMENT = gql`
  fragment UgcFragment on UgcType {
    ugcCoverImage {
      ...BynderVisualFragment
    }
    ugcDescription
    ugcProfileLink
    ugcTitle
    ugcVideoLinkDash
    ugcVideoLinkHls
    ugcProfilePicture {
      ...BynderVisualFragment
    }
    ugcUsername
  }
  ${BYNDER_VISUAL_FRAGMENT}
`

export const META_DATA_FRAGMENT = gql`
  fragment MetaDataFragment on VariantMetaDataType {
    title
    description
  }
`

export const VARIANT_FRAGMENT = gql`
  fragment VariantFragment on VariantType {
    id
    sku
    categoryName
    title
    subTitle
    stockLimit
    infoBanner
    keyBenefits
    size
    description
    brandName
    slug
    recommendedFor
    cListRecommended
    candleSafety
    highlights
    pdpUrl {
      uk
      us
    }
    product {
      id
    }
    media {
      ...BynderVisualFragment
    }
    detailsMedia {
      ...BynderVisualFragment
    }
    pricing {
      ...PricingFragment
    }
    pricingDetails {
      ...PlpPricingFragment
    }
    availability {
      ...AvailabilityFragment
    }
    badges {
      ...BadgeFragment
    }
    howTo {
      ...HowToFragment
    }
    formula {
      ...FormulaFragment
    }
    caution {
      ...LabelFragment
    }
    standards {
      ...StandardsFragment
    }
    review {
      ...ReviewSummaryFragment
    }
    origin {
      name
      code
    }
  }
  ${PRICING_FRAGMENT}
  ${PLP_PRICING_FRAGMENT}
  ${AVAILABILITY_FRAGMENT}
  ${BADGE_FRAGMENT}
  ${BYNDER_VISUAL_FRAGMENT}
  ${FORMULA_FRAGMENT}
  ${HOWTO_FRAGMENT}
  ${LABEL_FRAGMENT}
  ${STANDARDS_FRAGMENT}
  ${REVIEW_SUMMARY_FRAGMENT}
`

export const GWP_MODULE_FRAGMENT = gql`
  fragment GwpModuleFragment on GwpModuleType {
    id
    title
    description
    items {
      sku
      brandName
      title
      subTitle
      pdpUrl {
        uk
        us
      }
      imageUrl
      imageAlt
      imageTitle
      membersPrice
      typicalPrice
      slug
      size
      cta {
        actionType
        text
      }
    }
  }
`

export const THRESHOLDS_FRAGMENT = gql`
  fragment ThresholdsFragment on ThresholdType {
    type
    id
    __typename
    percentProgress
    progressBanner {
      showBanner
      copy
      termsAndConditions {
        template
        links {
          text
          href
        }
      }
    }
  }
`

export const THRESHOLDS_SECTION_FRAGMENT = gql`
  fragment ThresholdsSectionFragment on ThresholdsSection {
    id
    __typename
    items {
      ...ThresholdsFragment
    }
  }
  ${THRESHOLDS_FRAGMENT}
`

export const ASSOCIATIONS_FRAGMENT = gql`
  fragment AssociationsFragment on VariantAssociationGroupType {
    referenceType
    title
    variants {
      ...VariantFragment
    }
  }
  ${VARIANT_FRAGMENT}
`

export const VIDEOS_FRAGMENT = gql`
  fragment VideosFragment on VideoAssetType {
    id
    videoType
    imageUrl
    sources {
      uri
      key
      contentType
    }
    name
    description
    tags
    position
  }
`

export const RELATED_VARIANT_FRAGMENT = gql`
  fragment RelatedVariantFragment on VariantType {
    id
    availability {
      status
    }
    pdpUrl {
      uk
      us
    }
    shadeVisual
    size
    slug
    title
    subTitle
  }
`

export const EXTENDED_VARIANT_FRAGMENT = gql`
  fragment ExtendedVariantFragment on VariantType {
    ...VariantFragment
    keywords
    elevatorPitch
    shortDescription
    longDescription
    footNotes
    size
    type
    shadeVisual
    product {
      status
      category {
        urlPath
      }
      categories {
        name
      }
    }
    hreflangs {
      locale
      slug
    }
    origin {
      ...CountryFragment
    }
    review {
      ...ReviewSummaryFragment
    }
    suitableFor
    detailsMedia {
      ...BynderVisualFragment
    }
    trials {
      ...TrialsFragment
    }
    metaData {
      ...MetaDataFragment
    }
    associations {
      ...AssociationsFragment
    }
    videos {
      ...VideosFragment
    }
    visuals {
      ...VisualFragment
    }
    howToUseSlides {
      ...HowToUseSlidesFragment
    }
    ugc {
      ...UgcFragment
    }
  }
  ${VARIANT_FRAGMENT}
  ${COUNTRY_FRAGMENT}
  ${REVIEW_SUMMARY_FRAGMENT}
  ${BYNDER_VISUAL_FRAGMENT}
  ${TRIALS_FRAGMENT}
  ${META_DATA_FRAGMENT}
  ${ASSOCIATIONS_FRAGMENT}
  ${VIDEOS_FRAGMENT}
  ${VISUAL_FRAGMENT}
  ${HOW_TO_USE_SLIDES_FRAGMENT}
  ${UGC_FRAGMENT}
`
export const FULL_VARIANT_FRAGMENT = gql`
  fragment FullVariantFragment on VariantType {
    ...ExtendedVariantFragment
    relatedVariants {
      ...RelatedVariantFragment
    }
  }
  ${EXTENDED_VARIANT_FRAGMENT}
  ${RELATED_VARIANT_FRAGMENT}
`

export const DIGITAL_GIFT_FRAGMENT = gql`
  fragment DigitalGiftFragment on DigitalGiftType {
    to
    message
    address
    date
  }
`

export const PHYSICAL_GIFT_FRAGMENT = gql`
  fragment PhysicalGiftFragment on PhysicalGiftType {
    to
    message
    wrapping
    date
  }
`

export const ITEM_FRAGMENT = gql`
  fragment ItemFragment on ItemType {
    id
    type
    origin
    variant {
      ...VariantFragment
    }
    membership {
      ...MembershipFragment
    }
    giftInfo {
      ...DigitalGiftFragment
    }
    quantity
    origin
  }
  ${VARIANT_FRAGMENT}
  ${MEMBERSHIP_FRAGMENT}
  ${DIGITAL_GIFT_FRAGMENT}
`

export const NESTED_ITEM_FRAGMENT = gql`
  fragment NestedItemFragment on NestedItemType {
    id
    type
    origin
    variant {
      ...VariantFragment
    }
    membership {
      ...MembershipFragment
    }
    giftInfo {
      ...DigitalGiftFragment
    }
    quantity
    origin
  }
  ${VARIANT_FRAGMENT}
  ${MEMBERSHIP_FRAGMENT}
  ${DIGITAL_GIFT_FRAGMENT}
`

export const ADDRESS_FRAGMENT = gql`
  fragment AddressFragment on AddressType {
    city
    countryCode
    email
    externalId
    firstName
    id
    lastName
    lineOne
    lineThree
    lineTwo
    isDefaultBilling
    isDefaultShipping
    name
    phone
    postCode
    state
  }
`

export const SHIPPING_METHOD_FRAGMENT = gql`
  fragment ShippingMethodFragment on ShippingMethodType {
    id
    name
    deliveryEstimate
  }
`

export const ORDER_DRAFT_FRAGMENT = gql`
  fragment OrderDraftFragment on OrderDraftType {
    id
    state
    giftInfo {
      ...PhysicalGiftFragment
    }
    deliveryAddress {
      ...AddressFragment
    }
    billingAddress {
      ...AddressFragment
    }
    shippingMethod {
      ...ShippingMethodFragment
    }
    totals {
      basket
      shipping
      tax
    }
  }
  ${ADDRESS_FRAGMENT}
  ${SHIPPING_METHOD_FRAGMENT}
  ${PHYSICAL_GIFT_FRAGMENT}
`

export const DISCOUNT_EFFECT_FRAGMENT = gql`
  fragment DiscountEffectFragment on DiscountEffectType {
    target
    targetItemId
    targetPimId
    targetType
    value
    valueType
  }
`

export const DISCOUNT_FRAGMENT = gql`
  fragment DiscountFragment on DiscountType {
    id
    code
    state
    effects {
      ...DiscountEffectFragment
    }
  }
  ${DISCOUNT_EFFECT_FRAGMENT}
`

export const USER_FRAGMENT = gql`
  fragment UserFragment on UserType {
    id
    name
    email
    membership {
      ...MembershipFragment
    }
    avatar {
      ...VisualFragment
    }
    spentSoFar
    carriedForward
    dateOfBirth
    MembershipEndDate
    MembershipStartDate
    registrationDateTime
    TotalOrders
    LifetimeValueOrders
    LifetimeSubscriptionSaving
    deliveryType
  }
  ${MEMBERSHIP_FRAGMENT}
  ${VISUAL_FRAGMENT}
`

export const EXPERIMENT_FRAGMENT = gql`
  fragment ExperimentFragment on ExperimentType {
    id
    value
    metadata
  }
`

// complete user pull

export const EXTENDED_USER_FRAGMENT = gql`
  fragment ExtendedUserFragment on UserType {
    id
    name
    firstName
    lastName
    email
    membership {
      ...MembershipFragment
    }
    avatar {
      ...VisualFragment
    }
    spentSoFar
    carriedForward
    dateOfBirth
    MembershipEndDate
    MembershipStartDate
    registrationDateTime
    TotalOrders
    LifetimeValueOrders
    LifetimeSubscriptionSaving
    deliveryType
    basketId
    basketVersion
    isFreeTrialAllowed
    basket {
      ...ItemFragment
    }
    wishlistId
    wishlistVersion
    wishlist {
      ...ItemFragment
    }
    discounts {
      ...DiscountFragment
    }
    orderDraft {
      ...OrderDraftFragment
    }
    hints {
      ...TagFragment
    }
    reviews {
      ...ReviewFragment
    }
    includedDiscounts {
      ...IncludedDiscountsFragment
    }
    rejectedDiscounts {
      ...RejectedDiscountFragment
    }
  }
  ${MEMBERSHIP_FRAGMENT}
  ${VISUAL_FRAGMENT}
  ${TAG_FRAGMENT}
  ${ITEM_FRAGMENT}
  ${REVIEW_FRAGMENT}
  ${DISCOUNT_FRAGMENT}
  ${ORDER_DRAFT_FRAGMENT}
  ${INCLUDED_DISCOUNTS_FRAGMENT}
  ${REJECTED_DISCOUNTS_FRAGMENT}
`

export const NOTIFICATION_MANAGER_FRAGMENT = gql`
  fragment NotificationManagerFragment on NotificationManagerType {
    id
    notifications {
      code
      level
      handler
      params
    }
  }
`

export const USER_NOTIFICATION_FRAGMENT = gql`
  fragment UserNotificationFragment on UserNotificationType {
    code
    level
    handler
    params
  }
`

export const USER_PROFILE_FRAGMENT = gql`
  fragment UserProfileFragment on UserProfileType {
    id
    scope
    firstName
    lastName
    phone
    email
    avatar {
      ...VisualFragment
    }
    birthday
    addresses {
      ...AddressFragment
    }
    defaultAddress
    marketingConsent
    optIn {
      email
      push
      sms
    }
  }
  ${VISUAL_FRAGMENT}
  ${ADDRESS_FRAGMENT}
`

export const VARIANT_FRAGMENT_V2 = gql`
  fragment VariantFragmentV2 on VariantTypeV2 {
    id
    sku
    categoryName
    title
    subTitle
    stockLimit
    infoBanner
    keyBenefits
    size
    description
    brandName
    slug
    recommendedFor
    cListRecommended
    candleSafety
    highlights
    pdpUrl {
      uk
      us
    }
    product {
      id
    }
    media {
      ...BynderVisualFragment
    }
    detailsMedia {
      ...BynderVisualFragment
    }
    pricing {
      ...PricingFragment
    }
    pricingDetails {
      ...PlpPricingFragment
    }
    availability {
      ...AvailabilityFragment
    }
    badges {
      ...BadgeFragment
    }
    howTo {
      ...HowToFragment
    }
    formula {
      ...FormulaFragment
    }
    caution {
      ...LabelFragment
    }
    standards {
      ...StandardsFragment
    }
    review {
      ...ReviewSummaryFragment
    }
    origin {
      name
      code
    }
    visuals {
      ...VisualFragment
    }
  }
  ${PRICING_FRAGMENT}
  ${PLP_PRICING_FRAGMENT}
  ${AVAILABILITY_FRAGMENT}
  ${BADGE_FRAGMENT}
  ${BYNDER_VISUAL_FRAGMENT}
  ${FORMULA_FRAGMENT}
  ${HOWTO_FRAGMENT}
  ${LABEL_FRAGMENT}
  ${STANDARDS_FRAGMENT}
  ${REVIEW_SUMMARY_FRAGMENT}
  ${VISUAL_FRAGMENT}
`

export const SPOTLIGHT_MODULE_FRAGMENT = gql`
  fragment SpotlightModuleFragment on SpotlightModuleType {
    id
    tracking {
      experimentName
      experimentVariant
    }
    content {
      moduleTitle
      moduleSubtitle
      ctaText
      variants {
        ...VariantFragmentV2
      }
    }
  }
  ${VARIANT_FRAGMENT_V2}
`
// Fragment for TrackPageElementClickedEventType
export const TRACK_PAGE_ELEMENT_CLICKED_FRAGMENT = gql`
  fragment TrackPageElementClickedFragment on TrackPageElementClickedEventType {
    event
    properties {
      location_type
      module_content_details
      module_content_url
      module_content
      module_id
      module_position
      module_content_details2
      module_content_details3
      location_type_details
      module_content2
    }
  }
`

export const TRACKING_FRAGMENT = gql`
  fragment TrackingFragment on Tracking {
    viewed {
      ... on TrackPageElementViewedEventType {
        event
        properties {
          location_type
          module_content_details
          module_content
          module_id
          module_position
          location_type_details
          module_content2
        }
      }
      ... on TrackProductListViewedEventType {
        event
        properties {
          product_category_1
          product_category_2
          product_category_3
          products {
            sku
          }
          currency
          product_price_type
          location_type
          location_type_details
          result_id
          pod_id
          num_results_viewed
          result_type
        }
      }
      ... on TrackExperimentViewedEventType {
        event
        properties {
          experiment_id
          experiment_name
          experiment_variant
          experiment_variant_name
        }
      }
    }
    clicked {
      # TODO: share this type with Basket
      ... on TrackProductAddedEventType {
        event
        properties {
          sku
          name
          brand
          variant
          price
          members_price
          typical_price
          quantity
          url
          badge
          ribbon
          product_price_type
          currency
          location_type
          location_type_details
          social_proof {
            social_proof_type
            social_proof_text
            social_proof_value
          }
        }
      }
      ... on TrackProductRemovedEventType {
        event
        properties {
          sku
          name
          brand
          variant
          price
          members_price
          typical_price
          quantity
          url
          product_price_type
          currency
          location_type
        }
      }
      ...TrackPageElementClickedFragment
      ... on TrackProductClickedEventType {
        event
        properties {
          sku
          name
          brand
          variant
          members_price
          typical_price
          currency
          url
          position
          location_type
          badge
          ribbon
          location_type_details
          result_type
          result_id
          pod_id
          strategy_id
        }
      }
    }
  }
  ${TRACK_PAGE_ELEMENT_CLICKED_FRAGMENT}
`

export const SCROLL_TRACKING_FRAGMENT = gql`
  fragment ScrollTrackingFragment on ScrollTracking {
    next {
      ...TrackPageElementClickedFragment
    }
    prev {
      ...TrackPageElementClickedFragment
    }
  }
  ${TRACK_PAGE_ELEMENT_CLICKED_FRAGMENT}
`

export const BASKET_BENEFIT_FRAGMENT = gql`
  fragment BasketBenefitFragment on BasketBenefitType {
    id
    componentType
    data {
      title
      description
      items {
        id
        brandName
        imageAlt
        imageTitle
        imageUrl
        membersPrice
        sku
        slug
        subTitle
        title
        typicalPrice
        size
        itemClickBehaviour
        cta {
          text
          actionType
          tracking {
            ...TrackingFragment
          }
        }
        tracking {
          ...TrackingFragment
        }
      }
      tracking {
        ...TrackingFragment
      }
    }
  }
  ${TRACKING_FRAGMENT}
`

export const BENEFITS_SECTION_MODULE_FRAGMENT = gql`
  fragment BenefitsSectionModuleFragment on BenefitsSectionModule {
    id
    benefits {
      ...BasketBenefitFragment
    }
  }
  ${BASKET_BENEFIT_FRAGMENT}
`

export const MEMBERSHIP_TOGGLE_CARD_FRAGMENT = gql`
  fragment MembershipToggleCardFragment on BasketMembershipToggleCardType {
    showToggle
    isToggled
    heading
    membershipId
    accordionHeading
    aboveAccordionDescription
    withinAccordionDescription
    termsBanner
    id
    savingsText
    image {
      filename
      id
      src
    }
    membershipInBag {
      ...ItemFragment
    }
    openAccordionTracking {
      ... on TrackPageElementClickedEventType {
        event
        properties {
          location_type
          module_content
        }
      }
    }
    onToggleTracking {
      ... on TrackPageElementClickedEventType {
        event
        properties {
          location_type
          module_content_details
          module_content
        }
      }
    }
    price
    membershipPrice
  }
  ${ITEM_FRAGMENT}
`

const BASKET_UPDATE_TRACKING_FRAGMENT = gql`
  fragment BasketUpdateTrackingFragment on TrackProductAddedEventType {
    event
    properties {
      sku
      name
      brand
      variant
      price
      members_price
      typical_price
      quantity
      url
      badge
      ribbon
      product_price_type
      currency
      location_type
      location_type_details
      social_proof {
        social_proof_type
        social_proof_text
        social_proof_value
      }
    }
  }
`

export const PARENT_FLYOUT_FRAGMENT = gql`
  fragment ParentFlyOutFragment on ParentFlyout {
    data {
      items {
        brandName
        id
        imageAlt
        imageTitle
        imageUrl
        membersPrice
        size
        sku
        slug
        subTitle
        title
        typicalPrice
        tracking {
          ...TrackingFragment
        }
      }
      sampleSku
      tracking {
        ...TrackingFragment
      }
      cta {
        text
        tracking {
          ...TrackingFragment
        }
      }
    }
    title
    id
  }
  ${TRACKING_FRAGMENT}
`

const PRODUCT_CARD_PRODUCT_FRAGMENT = gql`
  fragment ProductCardProductFragment on ProductCardProduct {
    id
    image {
      alt
      src
    }
    sku
    href
    brandName
    productName
    variationName
    primaryPrice
    secondaryPrice
    rating
    reviewCount
    primarySubtitle
    secondarySubtitle
    inWishlist
    inStock
  }
`
export const RECOMMENDATION_SECTION_FRAGMENT = gql`
  fragment RecommendationSectionFragment on RecommendationSection {
    id
    componentType
    description
    title
    locationType
    cta {
      text
      tracking {
        ...TrackingFragment
      }
    }
    tracking {
      ...TrackingFragment
    }
    items {
      id
      product {
        ...ProductCardProductFragment
      }
      cta {
        actionType
        text
        tracking {
          ...TrackingFragment
        }
      }
      tracking {
        ...TrackingFragment
      }
    }
    scrollConfig {
      tracking {
        ...ScrollTrackingFragment
      }
    }
  }
  ${PRODUCT_CARD_PRODUCT_FRAGMENT}
  ${TRACKING_FRAGMENT}
  ${SCROLL_TRACKING_FRAGMENT}
`
export const RECOMMENDATION_SECTION_MODULE_FRAGMENT = gql`
  fragment RecommendationSectionModuleFragment on RecommendationSectionModule {
    id
    items {
      ...RecommendationSectionFragment
    }
  }
  ${RECOMMENDATION_SECTION_FRAGMENT}
`

export const BASKET_UPDATE_FRAGMENT = gql`
  fragment BasketUpdateFragment on BasketUpdateResultType {
    status
    listId
    version
    discounts {
      ...DiscountFragment
    }
    notifications {
      ...UserNotificationFragment
    }
    items {
      ...ItemFragment
    }
    itemUpdates {
      id
      itemType
    }
    pricing {
      ...BasketPricingFragment
    }
    includedDiscounts {
      ...IncludedDiscountsFragment
    }
    rejectedDiscounts {
      ...RejectedDiscountFragment
    }
    deliveryMethod
    hasOnlyVirtualProducts
    deliveryMethodName
    termsAndConditions {
      template
      links {
        text
        href
      }
    }
    benefitsSectionModule {
      ...BenefitsSectionModuleFragment
    }
    thresholds {
      ...ThresholdsFragment
    }
    thresholdsSection {
      ...ThresholdsSectionFragment
    }
    gwpModule {
      ...GwpModuleFragment
    }
    spotlightModule {
      ...SpotlightModuleFragment
    }
    parentFlyout {
      ...ParentFlyOutFragment
    }
    recommendationSection {
      ...RecommendationSectionModuleFragment
    }
    membershipToggleCard {
      ...MembershipToggleCardFragment
    }
    updateTracking {
      ...BasketUpdateTrackingFragment
    }
  }
  ${VARIANT_FRAGMENT}
  ${VARIANT_FRAGMENT_V2}
  ${ITEM_FRAGMENT}
  ${DISCOUNT_FRAGMENT}
  ${USER_NOTIFICATION_FRAGMENT}
  ${BASKET_PRICING_FRAGMENT}
  ${INCLUDED_DISCOUNTS_FRAGMENT}
  ${REJECTED_DISCOUNTS_FRAGMENT}
  ${SPOTLIGHT_MODULE_FRAGMENT}
  ${GWP_MODULE_FRAGMENT}
  ${THRESHOLDS_FRAGMENT}
  ${THRESHOLDS_SECTION_FRAGMENT}
  ${BENEFITS_SECTION_MODULE_FRAGMENT}
  ${PARENT_FLYOUT_FRAGMENT}
  ${RECOMMENDATION_SECTION_MODULE_FRAGMENT}
  ${MEMBERSHIP_TOGGLE_CARD_FRAGMENT}
  ${BASKET_UPDATE_TRACKING_FRAGMENT}
`

export const DISCOUNT_UPDATE_FRAGMENT = gql`
  fragment DiscountUpdateFragment on DiscountUpdateResultType {
    status
    listId
    version
    basket {
      ...ItemFragment
    }
    discounts {
      ...DiscountFragment
    }
    notifications {
      ...UserNotificationFragment
    }
    pricing {
      ...BasketPricingFragment
    }
    includedDiscounts {
      ...IncludedDiscountsFragment
    }
    rejectedDiscounts {
      ...RejectedDiscountFragment
    }
    benefitsSection {
      ...BasketBenefitFragment
    }
    thresholds {
      ...ThresholdsFragment
    }
    thresholdsSection {
      ...ThresholdsSectionFragment
    }
    gwpModule {
      ...GwpModuleFragment
    }
  }
  ${ITEM_FRAGMENT}
  ${DISCOUNT_FRAGMENT}
  ${USER_NOTIFICATION_FRAGMENT}
  ${BASKET_PRICING_FRAGMENT}
  ${INCLUDED_DISCOUNTS_FRAGMENT}
  ${REJECTED_DISCOUNTS_FRAGMENT}
  ${GWP_MODULE_FRAGMENT}
  ${BASKET_BENEFIT_FRAGMENT}
  ${THRESHOLDS_FRAGMENT}
  ${THRESHOLDS_SECTION_FRAGMENT}
`
export const WISHLIST_UPDATE_FRAGMENT = gql`
  fragment WishListUpdateFragment on WishListUpdateResultType {
    status
    listId
    version
    wishes {
      ...ItemFragment
    }
    notifications {
      ...UserNotificationFragment
    }
  }
  ${ITEM_FRAGMENT}
  ${USER_NOTIFICATION_FRAGMENT}
`

export const CLIENT_ACCESS_FRAGMENT = gql`
  fragment ClientAccessFragment on ClientAccessType {
    platforms {
      platform
      allowed
    }
  }
`

export const LOGIN_RESPONSE_FRAGMENT = gql`
  fragment LoginResponseFragment on LoginUpdateResultType {
    status
    loginBackend
    token
    ttl
    refreshTTL
    role
    user {
      ...ExtendedUserFragment
    }
    redirectLocation
    notifications {
      ...UserNotificationFragment
    }
    clientAccess {
      ...ClientAccessFragment
    }
  }
  ${EXTENDED_USER_FRAGMENT}
  ${USER_NOTIFICATION_FRAGMENT}
  ${CLIENT_ACCESS_FRAGMENT}
`

const DELIVERY_METHOD_FRAGMENT = gql`
  fragment DeliveryMethodFragment on ShippingMethodType {
    id
    name
    provider
    deliveryEstimate
    cost
    discountedCost
    isDefault
  }
`

export const PAYMENT_STATE_FRAGMENT = gql`
  fragment PaymentStateFragment on PaymentStateType {
    id
    version
    amountCents
    countryCode
    locale
    clientKey
    serviceProvider
    paymentMethodsJson
    submitAdditionalPaymentDetailsJson
    resultCode
    actionJson
    paymentMethodsAvailable {
      name
      type
      brands
    }
  }
`

export const EXPRESS_CHECKOUT_STATE_FRAGMENT = gql`
  fragment ExpressCheckoutStateFragment on CheckoutStateExpressType {
    deliveryMethod
    deliveryMethods {
      deliveryEstimate
      discountedCost
      id
      name
    }
    payment {
      id
      version
      amountCents
      countryCode
      locale
      paymentMethodsJson
      clientKey
      resultCode
      serviceProvider
    }
    discounts {
      lineItemsTotalDiscount
    }
    initialPayment {
      total
    }
  }
`

export const CHECKOUT_STATE_FRAGMENT = gql`
  fragment CheckoutStateFragment on CheckoutStateType {
    cartKey
    cartId
    cartVersion
    shippingAddress {
      ...AddressFragment
    }
    billingAddress {
      ...AddressFragment
    }
    deliveryMethod
    deliveryMethodId
    deliveryMethods {
      ...DeliveryMethodFragment
    }
    payment {
      ...PaymentStateFragment
    }
    discounts {
      ...DiscountFragment
    }
    shippingTax
    salesTax
    cartTotal
    hasOnlyVirtualProducts
    profileAddresses {
      ...AddressFragment
    }
    pricing {
      ...BasketPricingFragment
    }
    includedDiscounts {
      ...IncludedDiscountsFragment
    }
    rejectedDiscounts {
      ...RejectedDiscountFragment
    }
    giftMessage
    experiments {
      experimentVariant
      experimentName
    }
    expressCheckoutAvailable
  }
  ${ADDRESS_FRAGMENT}
  ${DELIVERY_METHOD_FRAGMENT}
  ${DISCOUNT_FRAGMENT}
  ${PAYMENT_STATE_FRAGMENT}
  ${BASKET_PRICING_FRAGMENT}
  ${INCLUDED_DISCOUNTS_FRAGMENT}
  ${REJECTED_DISCOUNTS_FRAGMENT}
`

export const CHECKOUT_MAKE_PAYMENT_EXPRESS_FRAGMENT = gql`
  fragment CheckoutMakePaymentExpressFragment on CheckoutMakePaymentExpressType {
    cartKey
    cartId
    cartVersion
    payment {
      id
      resultCode
      version
    }
    notifications {
      ...UserNotificationFragment
    }
  }
  ${USER_NOTIFICATION_FRAGMENT}
`

export const CHECKOUT_RESULT_FRAGMENT = gql`
  fragment CheckoutResultFragment on CheckoutUpdateResultType {
    status
    basketId
    basketVersion
    wishlistId
    wishlistVersion
    items {
      ...ItemFragment
    }
    wishes {
      ...ItemFragment
    }
    discounts {
      ...DiscountFragment
    }
    notifications {
      ...UserNotificationFragment
    }
    userProfile {
      ...UserProfileFragment
    }
    checkoutState {
      ...CheckoutStateFragment
      expressCheckoutState {
        ...ExpressCheckoutStateFragment
      }
    }
    checkoutBackend
    profileAddresses {
      ...AddressFragment
    }
    pricing {
      ...BasketPricingFragment
    }
    includedDiscounts {
      ...IncludedDiscountsFragment
    }
    rejectedDiscounts {
      ...RejectedDiscountFragment
    }
    termsAndConditions {
      template
      links {
        text
        href
      }
    }
    nextPage
    gwpModule {
      ...GwpModuleFragment
    }
  }
  ${ITEM_FRAGMENT}
  ${DISCOUNT_FRAGMENT}
  ${USER_PROFILE_FRAGMENT}
  ${USER_NOTIFICATION_FRAGMENT}
  ${CHECKOUT_STATE_FRAGMENT}
  ${EXPRESS_CHECKOUT_STATE_FRAGMENT}
  ${PRICING_FRAGMENT}
  ${INCLUDED_DISCOUNTS_FRAGMENT}
  ${REJECTED_DISCOUNTS_FRAGMENT}
  ${GWP_MODULE_FRAGMENT}
`

export const ORDER_ITEM_FRAGMENT = gql`
  fragment OrderItemFragment on OrderItem {
    id
    imgUrl
    name
    variantName
    franchiseName
    slug
    size
    quantity
    isFreeGift
    variantSku
    itemClickBehaviour
    price {
      currencyCode
      original {
        gross
        net
        tax
      }
      discounted {
        gross
        net
        tax
      }
    }
  }
`

export const ORDER_WITH_PARCELS = gql`
  fragment OrderWithParcelsFragment on OrderWithParcels {
    parcels {
      trackingId
      provider
      items {
        ...OrderItemFragment
      }
    }
    pendingItems {
      ...OrderItemFragment
    }
    virtualProducts {
      ...OrderItemFragment
    }
  }
  ${ORDER_ITEM_FRAGMENT}
`

export const ORDER_PAYMENT_FRAGMENT = gql`
  fragment OrderPaymentFragment on OrderPayment {
    info {
      currencyCode
      original {
        gross
        net
        tax
      }
      discounted {
        gross
        net
        tax
      }
    }
  }
`

export const ORDER_SHIPPING_FRAGMENT = gql`
  fragment OrderShippingFragment on OrderShipping {
    address {
      firstName
      lastName
      lineOne
      lineTwo
      postCode
      countryCode
      state
      city
      email
      phone
    }
    info {
      name
      price {
        currencyCode
        original {
          gross
          net
          tax
        }
        discounted {
          gross
          net
          tax
        }
      }
    }
  }
`

export const ORDER_RECORD_FRAGMENT = gql`
  fragment OrderRecordFragment on OrderRecordType {
    date
    id
    source
    orderNumber
    itemsCountText
    orderStage
    payment {
      ...OrderPaymentFragment
    }
    urls {
      orderDetails
    }
  }
  ${ORDER_PAYMENT_FRAGMENT}
`

export const ORDER_DETAIL_FRAGMENT = gql`
  fragment OrderDetailFragment on OrderRecordType {
    date
    digitalOnly
    id
    source
    orderNumber
    orderStage
    orderWithParcels {
      ...OrderWithParcelsFragment
    }
    payment {
      ...OrderPaymentFragment
    }
    shipping {
      ...OrderShippingFragment
    }
    pricing {
      ...BasketPricingFragment
    }
    itemsCountText
    paymentDetails {
      name
      cardDetail
    }
    giftMessage
    parentFlyout {
      ...ParentFlyOutFragment
    }
  }
  ${ORDER_WITH_PARCELS}
  ${ORDER_ITEM_FRAGMENT}
  ${ORDER_PAYMENT_FRAGMENT}
  ${ORDER_SHIPPING_FRAGMENT}
  ${BASKET_PRICING_FRAGMENT}
  ${PARENT_FLYOUT_FRAGMENT}
`

export const MEMBER_SUBSCRIPTION_FRAGMENT_BASE = gql`
  fragment MemberSubscriptionFragmentBase on SubscriptionType {
    id
    name
    sku
    status
    active
    isFreeTrial
    isGift
    isExtended
    currentTermStart
    currentTermEnd
    dateCreated
    dateStarted
    dateActivated
    dateUpdated
    dateCancelled
    entitlements {
      name
      value
    }
    modifiableTo {
      sku
      name
    }
    renewsTo {
      sku
      name
    }
    refundStatus
    billing {
      billingPeriod
      currency
      centAmount
      frequency
      nextPaymentDate
    }
    trialPeriod
    extendsBy
    extensionAllowed
  }
`

export const MEMBER_SUBSCRIPTION_FRAGMENT = gql`
  fragment MemberSubscriptionFragment on SubscriptionType {
    scheduledChanges {
      ...MemberSubscriptionFragmentBase
    }
    ...MemberSubscriptionFragmentBase
  }
  ${MEMBER_SUBSCRIPTION_FRAGMENT_BASE}
`

export const MEMBER_INFO_FRAGMENT = gql`
  fragment MemberInfoFragment on MemberInfoType {
    customerNumber
    subscriptions {
      active {
        ...MemberSubscriptionFragment
        scheduledChanges {
          ...MemberSubscriptionFragment
        }
      }
      other {
        ...MemberSubscriptionFragment
      }
    }
  }
  ${MEMBER_SUBSCRIPTION_FRAGMENT}
`

export const REORDER_PRODUCTS_FRAGMENT = gql`
  fragment ReorderProductsFragment on VariantType {
    pricing {
      ...PricingFragment
    }
    pricingDetails {
      ...PlpPricingFragment
    }
    media {
      ...BynderVisualFragment
    }
    detailsMedia {
      ...BynderVisualFragment
    }
    badges {
      ...BadgeFragment
    }
    id
    availability {
      ...AvailabilityFragment
    }
    brandName
    title
    subTitle
    sku
    pdpUrl {
      uk
      us
    }
    slug
    review {
      ...ReviewSummaryFragment
    }
  }
  ${BYNDER_VISUAL_FRAGMENT}
  ${AVAILABILITY_FRAGMENT}
  ${PRICING_FRAGMENT}
  ${BADGE_FRAGMENT}
  ${REVIEW_SUMMARY_FRAGMENT}
  ${PLP_PRICING_FRAGMENT}
`

export const GENERIC_IMAGE_FRAGMENT = gql`
  fragment GenericImageFragment on GenericImage {
    description
    src
    altText
  }
`

export const PLP_REVIEW_FRAGMENT = gql`
  fragment PlpReviewFragment on ReviewSummaryType {
    count
    stars
  }
`

export const RIBBON_FRAGMENT = gql`
  fragment RibbonFragment on RibbonType {
    key
    text
  }
`
export const MARKETING_TILES_FRAGMENT = gql`
  fragment MarketingTilesFragment on PLPMidPageBannerType {
    title
    href
    membershipId
    mobileImage {
      src
      description
    }
    desktopImage {
      src
      description
    }
  }
`

export const PDP_PRICING_TOGGLE_FRAGMENT = gql`
  fragment PDPricingToggleFragment on PricingToggle {
    id
    data {
      defaultPrice
      prices {
        id
        price {
          ... on MembershipPricingOption {
            membership {
              name
              priceExplainer
              perks
              sku
              cta {
                title
              }
              terms {
                termsContent
              }
              savingsText
            }
            priceText
            title
            subtitle
          }
          ... on PriceWithMembershipOptions {
            memberships {
              name
              priceExplainer
              perks
              sku
              cta {
                title
              }
              terms {
                termsContent
              }
              savingsText
              isDefault
            }
            priceText
            title
            subtitle
          }
          ... on ProductOnlyPricingOption {
            priceText
            title
            subtitle
          }
        }
      }
    }
  }
`

export const PDP_MEMBERSHIP_OPTIONS_FRAGMENT = gql`
  fragment PdpMembershipOptionsFragment on MembershipOptionsV2 {
    isDefault
    priceExplainer
    name
    perks
    sku
    savingsText
    terms {
      termsContent
    }
  }
`
export const CHECKOUT_PREPARED_EXPRESS_FRAGMENT = gql`
  fragment CheckoutPreparedExpressFragment on CheckoutPreparedExpressType {
    checkoutState {
      ...ExpressCheckoutStateFragment
    }
    pricing {
      ...BasketPricingFragment
    }
    expressCheckoutAvailable
    hasOnlyVirtualProducts
  }
  ${EXPRESS_CHECKOUT_STATE_FRAGMENT}
  ${BASKET_PRICING_FRAGMENT}
`
